import { useEffect, useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { adminApi } from "../services/adminApi";
import { toast } from "sonner";
import {
  RefreshCcw,
  Search,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Clock,
  Database,
  Users,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  Loader2,
  Filter,
  XCircle,
} from "lucide-react";
import type { ConnectorAttentionReason } from "../types/admin";

interface ConnectorItem {
  id: string;
  name: string;
  type: string;
  status: string;
  lastSynced: string | null;
  totalData: number;
  syncedData: number;
  attentionReason?: ConnectorAttentionReason | null;
  tokenInvalidAt?: string | null;
  consecutiveFailures?: number;
  firstSyncPickedUp?: boolean;
  lastSyncHeartbeatAt?: string | null;
}

interface UserGroup {
  userId: string;
  userName: string;
  userEmail: string;
  organizationName: string;
  organizationId: string;
  connectors: ConnectorItem[];
}

interface ResyncOverview {
  users: UserGroup[];
  totalConnectors: number;
  connectorTypes: string[];
  organizations: Array<{ id: string; name: string }>;
}

const CONNECTOR_ICONS: Record<string, string> = {
  googleGmail: "Gmail",
  googleDrive: "Drive",
  googleCalendar: "Calendar",
  slack: "Slack",
  notion: "Notion",
  linear: "Linear",
  github: "GitHub",
  jira: "Jira",
  asana: "Asana",
  discord: "Discord",
  hubspot: "HubSpot",
  salesforce: "Salesforce",
  postgres: "Postgres",
  mysql: "MySQL",
  dropbox: "Dropbox",
  sentry: "Sentry",
  freshdesk: "Freshdesk",
  freshchat: "Freshchat",
  pipedrive: "Pipedrive",
  newrelic: "New Relic",
};

function formatConnectorType(type: string): string {
  return CONNECTOR_ICONS[type] || type.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();
}

function formatDate(date: string | null): string {
  if (!date) return "Never";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getStatusBadge(status: string) {
  switch (status) {
    case "syncCompleted":
      return <Badge variant="success">Synced</Badge>;
    case "syncStarted":
      return <Badge variant="warning">Syncing</Badge>;
    case "syncFailed":
      return <Badge variant="error">Failed</Badge>;
    default:
      return <Badge variant="default">{status}</Badge>;
  }
}

const ATTENTION_LABELS: Record<ConnectorAttentionReason, string> = {
  reauth_required: "Reauth required",
  setup_incomplete: "Setup incomplete",
  sync_failed: "Sync failed",
  sync_stuck: "Sync stuck",
  sync_unhealthy: "Recurring failures",
  webhook_failed: "Live updates paused",
};

function AttentionBadge({ connector }: { connector: ConnectorItem }) {
  const reason = connector.attentionReason;
  if (!reason) {
    return <span className="text-xs text-gray-400">—</span>;
  }
  const label = ATTENTION_LABELS[reason] ?? reason;
  const tone =
    reason === "webhook_failed"
      ? "bg-blue-50 text-blue-700 border-blue-200"
      : "bg-amber-50 text-amber-800 border-amber-200";
  const tooltipParts: string[] = [];
  if (connector.consecutiveFailures && connector.consecutiveFailures > 0) {
    tooltipParts.push(`${connector.consecutiveFailures} consecutive failures`);
  }
  if (connector.lastSyncHeartbeatAt) {
    tooltipParts.push(
      `Last heartbeat: ${new Date(connector.lastSyncHeartbeatAt).toLocaleString()}`
    );
  }
  if (connector.tokenInvalidAt) {
    tooltipParts.push(
      `Token invalid since: ${new Date(connector.tokenInvalidAt).toLocaleString()}`
    );
  }
  return (
    <span
      title={tooltipParts.join(" • ") || undefined}
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${tone}`}
    >
      <AlertTriangle className="h-3 w-3" />
      {label}
    </span>
  );
}

export default function ConnectorResyncPage() {
  const [overview, setOverview] = useState<ResyncOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<string>("");
  const [filterOrg, setFilterOrg] = useState<string>("");
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [selectedConnectors, setSelectedConnectors] = useState<Set<string>>(new Set());
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set());
  const [isBulkSyncing, setIsBulkSyncing] = useState(false);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (filterOrg) params.organizationId = filterOrg;
      if (filterType) params.type = filterType;
      const data = await adminApi.getResyncOverview(params);
      setOverview(data);
    } catch (error) {
      console.error("Failed to fetch resync overview:", error);
      toast.error("Failed to load connector data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, [filterOrg, filterType]);

  // Auto-refresh every 10s while syncing
  useEffect(() => {
    if (syncingIds.size === 0) return;
    const interval = setInterval(fetchOverview, 10000);
    return () => clearInterval(interval);
  }, [syncingIds.size]);

  const filteredUsers = useMemo(() => {
    if (!overview) return [];
    if (!searchQuery.trim()) return overview.users;
    const q = searchQuery.toLowerCase();
    return overview.users.filter(
      (u) =>
        u.userName.toLowerCase().includes(q) ||
        u.userEmail.toLowerCase().includes(q) ||
        u.organizationName.toLowerCase().includes(q) ||
        u.connectors.some((c) => c.name.toLowerCase().includes(q) || c.type.toLowerCase().includes(q))
    );
  }, [overview, searchQuery]);

  const stats = useMemo(() => {
    if (!overview) return { total: 0, synced: 0, failed: 0, stale: 0, users: 0 };
    const allConnectors = overview.users.flatMap((u) => u.connectors);
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    return {
      total: allConnectors.length,
      synced: allConnectors.filter((c) => c.status === "syncCompleted").length,
      failed: allConnectors.filter((c) => c.status === "syncFailed").length,
      stale: allConnectors.filter((c) => !c.lastSynced || now - new Date(c.lastSynced).getTime() > 7 * dayMs).length,
      users: overview.users.length,
    };
  }, [overview]);

  const toggleUserExpand = (userId: string) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleSelectConnector = (id: string) => {
    setSelectedConnectors((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectUser = (user: UserGroup) => {
    setSelectedConnectors((prev) => {
      const next = new Set(prev);
      const allSelected = user.connectors.every((c) => next.has(c.id));
      if (allSelected) {
        user.connectors.forEach((c) => next.delete(c.id));
      } else {
        user.connectors.forEach((c) => next.add(c.id));
      }
      return next;
    });
  };

  const selectAll = () => {
    if (!overview) return;
    const all = overview.users.flatMap((u) => u.connectors.map((c) => c.id));
    setSelectedConnectors(new Set(all));
    setExpandedUsers(new Set(overview.users.map((u) => u.userId)));
  };

  const selectNone = () => {
    setSelectedConnectors(new Set());
  };

  const selectFailed = () => {
    if (!overview) return;
    const failed = overview.users.flatMap((u) => u.connectors.filter((c) => c.status === "syncFailed").map((c) => c.id));
    setSelectedConnectors(new Set(failed));
  };

  const selectStale = () => {
    if (!overview) return;
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    const stale = overview.users.flatMap((u) =>
      u.connectors.filter((c) => !c.lastSynced || now - new Date(c.lastSynced).getTime() > 7 * dayMs).map((c) => c.id)
    );
    setSelectedConnectors(new Set(stale));
  };

  const handleBulkResync = async () => {
    if (selectedConnectors.size === 0) {
      toast.error("No connectors selected");
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to resync ${selectedConnectors.size} connector(s)? This will re-fetch data from source and re-index into Milvus.`
    );
    if (!confirmed) return;

    try {
      setIsBulkSyncing(true);
      const ids = [...selectedConnectors];
      setSyncingIds(new Set(ids));
      const result = await adminApi.bulkResync(ids);
      toast.success(result.message);
      setSelectedConnectors(new Set());
      // Refresh after a short delay to show updated statuses
      setTimeout(fetchOverview, 2000);
    } catch (error) {
      console.error("Bulk resync failed:", error);
      toast.error("Failed to start bulk resync");
    } finally {
      setIsBulkSyncing(false);
    }
  };

  const handleSyncSingle = async (connectorId: string) => {
    try {
      setSyncingIds((prev) => new Set([...prev, connectorId]));
      await adminApi.syncConnector(connectorId);
      toast.success("Sync started");
      setTimeout(fetchOverview, 2000);
    } catch (error) {
      toast.error("Failed to sync connector");
    } finally {
      setSyncingIds((prev) => {
        const next = new Set(prev);
        next.delete(connectorId);
        return next;
      });
    }
  };

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <p className="text-sm text-gray-500">Loading connector data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Connector Resync</h1>
          <p className="text-sm text-gray-500 mt-1">
            Re-fetch and re-index connector data into Milvus
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchOverview}
            disabled={loading}
          >
            <RefreshCcw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">Total</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">Synced</p>
                <p className="text-2xl font-bold text-green-700">{stats.synced}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">Failed</p>
                <p className="text-2xl font-bold text-red-700">{stats.failed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">Stale (&gt;7d)</p>
                <p className="text-2xl font-bold text-amber-700">{stats.stale}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">Users</p>
                <p className="text-2xl font-bold text-purple-700">{stats.users}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search users, connectors..."
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Connector Type Filter */}
            <div className="flex items-center gap-1">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="text-sm border border-gray-200 rounded-md px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                {overview?.connectorTypes.map((t) => (
                  <option key={t} value={t}>{formatConnectorType(t)}</option>
                ))}
              </select>
            </div>

            {/* Organization Filter */}
            <select
              value={filterOrg}
              onChange={(e) => setFilterOrg(e.target.value)}
              className="text-sm border border-gray-200 rounded-md px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Organizations</option>
              {overview?.organizations.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>

            <div className="h-6 w-px bg-gray-200" />

            {/* Quick Select Buttons */}
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={selectAll} className="text-xs h-8">
                Select All
              </Button>
              <Button variant="ghost" size="sm" onClick={selectNone} className="text-xs h-8">
                None
              </Button>
              <Button variant="ghost" size="sm" onClick={selectFailed} className="text-xs h-8 text-red-600 hover:text-red-700 hover:bg-red-50">
                <AlertCircle className="h-3 w-3 mr-1" />
                Failed
              </Button>
              <Button variant="ghost" size="sm" onClick={selectStale} className="text-xs h-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50">
                <Clock className="h-3 w-3 mr-1" />
                Stale
              </Button>
            </div>

            <div className="h-6 w-px bg-gray-200" />

            {/* Bulk Action */}
            <Button
              size="sm"
              onClick={handleBulkResync}
              disabled={selectedConnectors.size === 0 || isBulkSyncing}
              className="bg-blue-600 hover:bg-blue-700 text-white h-9"
            >
              {isBulkSyncing ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4 mr-1" />
              )}
              Resync {selectedConnectors.size > 0 ? `(${selectedConnectors.size})` : ""}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* User Groups */}
      <div className="space-y-3">
        {filteredUsers.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Database className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No connectors found</p>
            </CardContent>
          </Card>
        ) : (
          filteredUsers.map((user) => {
            const isExpanded = expandedUsers.has(user.userId);
            const allSelected = user.connectors.every((c) => selectedConnectors.has(c.id));
            const someSelected = user.connectors.some((c) => selectedConnectors.has(c.id));
            const userSyncedCount = user.connectors.filter((c) => c.status === "syncCompleted").length;
            const userFailedCount = user.connectors.filter((c) => c.status === "syncFailed").length;

            return (
              <Card key={user.userId} className="overflow-hidden">
                {/* User Header */}
                <div
                  className="flex items-center gap-3 px-5 py-3.5 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleUserExpand(user.userId)}
                >
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleSelectUser(user);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />

                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 text-sm">{user.userName}</span>
                      <span className="text-xs text-gray-400">{user.userEmail}</span>
                    </div>
                    <span className="text-xs text-gray-500">{user.organizationName}</span>
                  </div>

                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-medium text-gray-500">{user.connectors.length} connectors</span>
                    </div>
                    {userSyncedCount > 0 && (
                      <div className="flex items-center gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                        <span className="text-xs text-green-700">{userSyncedCount}</span>
                      </div>
                    )}
                    {userFailedCount > 0 && (
                      <div className="flex items-center gap-1">
                        <XCircle className="h-3.5 w-3.5 text-red-500" />
                        <span className="text-xs text-red-700">{userFailedCount}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Connector Table */}
                {isExpanded && (
                  <div className="border-t border-gray-100">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10"></TableHead>
                          <TableHead>Connector</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Attention</TableHead>
                          <TableHead>Documents</TableHead>
                          <TableHead>Last Synced</TableHead>
                          <TableHead className="w-20">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {user.connectors.map((connector) => {
                          const isSyncing = syncingIds.has(connector.id) || connector.status === "syncStarted";
                          const percentage = connector.totalData > 0
                            ? Math.round((connector.syncedData / connector.totalData) * 100)
                            : 0;

                          return (
                            <TableRow key={connector.id} className="group">
                              <TableCell>
                                <input
                                  type="checkbox"
                                  checked={selectedConnectors.has(connector.id)}
                                  onChange={() => toggleSelectConnector(connector.id)}
                                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                />
                              </TableCell>
                              <TableCell>
                                <span className="text-sm font-medium text-gray-900">
                                  {connector.name && connector.name.length > 35
                                    ? connector.name.substring(0, 35) + "..."
                                    : connector.name || "Unnamed"}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded-md">
                                  {formatConnectorType(connector.type)}
                                </span>
                              </TableCell>
                              <TableCell>{getStatusBadge(connector.status)}</TableCell>
                              <TableCell>
                                <AttentionBadge connector={connector} />
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                  <span className="text-xs text-gray-500 tabular-nums">
                                    {connector.syncedData}/{connector.totalData}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="text-xs text-gray-500">{formatDate(connector.lastSynced)}</span>
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleSyncSingle(connector.id)}
                                  disabled={isSyncing}
                                  className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Sync this connector"
                                >
                                  <RefreshCcw className={`h-3.5 w-3.5 ${isSyncing ? "animate-spin text-blue-600" : "text-gray-400"}`} />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
